import { FC, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Icon, LoadingGroup, LoadingSkeleton, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Bold, Paragraph, Subheading } from "capitalrx-components-2.0/lib/typography";

import {
  Pharmacy,
  useCreateFavoritePharmacyMutation,
  useListFavoritePharmaciesQuery,
  useUpdateFavoritePharamcyMutation,
} from "@/App/api/pharmacy";
import { ThemedButton } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { capitalizeString, formatPhoneNumber, getCustomerSupportNumber } from "@/App/utils/utils";
import { DetailSection } from "./DetailSection";

interface PharmacyDetailsProps extends Partial<Pharmacy> {
  readonly useCustomerSupportHotline?: boolean;
  readonly isLoading: boolean;
}

export const PharmacyDetails: FC<PharmacyDetailsProps> = (props) => {
  const { t: translate } = useTranslation();
  const {
    provider_id: providerId,
    address_line_1: addressLine1,
    address_line_2: addressLine2,
    city,
    state,
    zip,
    useCustomerSupportHotline,
    isLoading,
    phone,
    name: pharmacyName,
    hours: pharmacyHours,
    services: pharmacyServices,
    languages: pharmacyLanguages,
    minority_owned_pharmacy: isMinorityOwnedPharmacy,
  } = props;

  const alert = useAlert();
  const { isViewingDataholder } = useContext(MemberEligibilityContext);
  const {
    data: favoritePharmacies,
    isLoading: isLoadingFavoritePharmacies,
    isRefetching: isRefetchingFavoritePharmacies,
    refetch: refetchFavoritePharmacies,
  } = useListFavoritePharmaciesQuery();
  const { mutateAsync: updateFavoritePharmacy, isPending: isUpdatingFavoritePharmacy } = useUpdateFavoritePharamcyMutation();
  const { mutateAsync: createFavoritePharmacy, isPending: isCreatingFavoritePharmacy } = useCreateFavoritePharmacyMutation();
  const isEmulating = !!sessionStorage.getItem("isEmulation");

  const isFavoritePharmacy = useMemo(() => {
    return (favoritePharmacies?.results || []).map((pharm) => pharm.pharmacy.provider_id).includes(providerId);
  }, [favoritePharmacies, providerId]);

  const toggleFavoritePharmacy = useCallback(async () => {
    try {
      if (isFavoritePharmacy) {
        await updateFavoritePharmacy(providerId);
      } else {
        await createFavoritePharmacy(providerId);
      }
      await refetchFavoritePharmacies();
    } catch (e) {
      const errorMessage = e?.data?.message
      || translate(`PRICING_DETAIL_PANELS.pharmacyDetails.${isFavoritePharmacy ? "remove" : "set"}FavoritePharmacy`);
      alert({
        message: errorMessage,
        type: "error",
        style: "toast",
      });
    }
  }, [isFavoritePharmacy, providerId]);

  const { memberEligibility } = useContext(MemberEligibilityContext);

  const pharmacyAddress = useMemo(() => {
    if ((addressLine1 || addressLine2) && city && state && zip) {
      return `${capitalizeString(addressLine1)} ${addressLine2}, ${capitalizeString(city)}, ${state} ${zip}`;
    }
    return "";
  }, [addressLine1, addressLine2, city, state, zip]);

  const customerSupportNumber: string = useMemo(() => {
    if (useCustomerSupportHotline) {
      return getCustomerSupportNumber(memberEligibility);
    } else if (phone) {
      return formatPhoneNumber(phone);
    }
    return "";
  }, [phone]);

  const isFavoriteButtonDisabled = useMemo(() => {
    return (
      isViewingDataholder
      || isEmulating
      || isUpdatingFavoritePharmacy
      || isCreatingFavoritePharmacy
      || isLoadingFavoritePharmacies
      || isRefetchingFavoritePharmacies
    );
  }, [
    isViewingDataholder,
    isEmulating,
    isUpdatingFavoritePharmacy,
    isCreatingFavoritePharmacy,
    isLoadingFavoritePharmacies,
    isRefetchingFavoritePharmacies,
  ]);

  return (
    <div>
      <LoadingGroup loading={isLoading}>
        <Stack spacing="small">
          <Shelf>
            <Subheading>{translate("PRICING_DETAIL_PANELS.pharmacyDetails.pharmacyDetails")}</Subheading>
            <Shelf.Spacer />
            {!isLoadingFavoritePharmacies && (
              <ThemedButton
                disabled={isFavoriteButtonDisabled}
                testId="favorite-pharmacy"
                appearance="text"
                onClick={toggleFavoritePharmacy}
                icon={isFavoritePharmacy ? "star-filled" : "star-empty"}
              >
                {isFavoritePharmacy && translate("PRICING_DETAIL_PANELS.pharmacyDetails.removeFavorite")}
                {!isFavoritePharmacy && translate("PRICING_DETAIL_PANELS.pharmacyDetails.saveFavorite")}
              </ThemedButton>
            )}
          </Shelf>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%" }} />}>
            <DetailSection alignment="top">
              <Bold>{translate("PRICING_DETAIL_PANELS.pharmacyDetails.name")}: </Bold>
              <Stack spacing="none">
                <Paragraph testId="pharmacy-name">{pharmacyName}</Paragraph>
                {isMinorityOwnedPharmacy && (
                  <Paragraph testId="minority-owned-text">
                    <Icon size="small" name="info-circle" /> {translate("PRICING_DETAIL_PANELS.pharmacyDetails.shopAtMinorityOwnedPharmacy")}
                  </Paragraph>
                )}
              </Stack>
              <Shelf.Spacer />
            </DetailSection>
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%" }} />}>
            <DetailSection alignment="top">
              <Bold>{translate("PRICING_DETAIL_PANELS.pharmacyDetails.address")}: </Bold>
              <Paragraph testId="pharmacy-address">{pharmacyAddress}</Paragraph>
              <Shelf.Spacer />
            </DetailSection>
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%" }} />}>
            <DetailSection alignment="top">
              <Bold>{translate("PRICING_DETAIL_PANELS.pharmacyDetails.phone")}: </Bold>
              <Paragraph testId="customer-support-number">{customerSupportNumber}</Paragraph>
              <Shelf.Spacer />
            </DetailSection>
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%" }} />}>
            <DetailSection alignment="top">
              <Bold>{translate("PRICING_DETAIL_PANELS.pharmacyDetails.hours")}: </Bold>
              <Stack spacing="none" testId="hours-container">
                {(pharmacyHours || []).map((hour) => {
                  return <Paragraph key={hour}>{hour}</Paragraph>;
                })}
              </Stack>
              <Shelf.Spacer />
            </DetailSection>
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%" }} />}>
            <Stack spacing="none">
              <Shelf alignment="top">
                <Bold>{translate("PRICING_DETAIL_PANELS.pharmacyDetails.fluShots")}: </Bold>
                <Paragraph testId="flu-shot-provider">{pharmacyServices?.immunizations_provided_indicator || "No"}</Paragraph>
                <Shelf.Spacer />
              </Shelf>
              <Shelf alignment="top">
                <Bold>{translate("PRICING_DETAIL_PANELS.pharmacyDetails.driveThru")}: </Bold>
                <Paragraph testId="drive-thru-provider">{pharmacyServices?.drive_up_window_indicator || "No"}</Paragraph>
                <Shelf.Spacer />
              </Shelf>
              <Shelf alignment="top">
                <Bold>{translate("PRICING_DETAIL_PANELS.pharmacyDetails.languages")}: </Bold>
                <Paragraph testId="languages">{(pharmacyLanguages || []).join(", ")}</Paragraph>
                <Shelf.Spacer />
              </Shelf>
            </Stack>
          </LoadingGroup.Placeholder>
        </Stack>
      </LoadingGroup>
    </div>
  );
};
